// @ts-strict-ignore
import Router from 'next/router';
import { DropdownItem } from 'reactstrap';
import ButtonDropdown from 'src/components/buttons/buttonDropdown';
import { useDispatch, useSelector } from 'src/store/store';
import { getPostTypeName } from 'src/utils/postUtil';
import { togglePostPin, restrictUser, updatePollLock, updatePostLock } from 'src/modules/community/singlePost/actions/actions';
import { showModal, showTurnOffRepliesModal } from 'src/modules/shared/modal/actions';
import { setAlert } from 'src/components/alert/actions';
import { getCanEditBadges, userHasPermission, userHasRole } from 'src/utils/context';
import i18n from 'src/utils/translate';
import { trackFootfallEvent } from 'src/utils/footfall';
import { Event, Section } from 'src/constants/footfall';
import { Permission, Post, PostType, Reply, Role } from 'src/constants/types';
import { ModalType } from 'src/constants/modalTypes';
import { UNLOCK } from 'src/constants/postCommons';
import { ModalProps } from 'src/modules/shared/modal/components/defaultModal';
type Props = {
  buttonText: string;
  dataTestId?: string;
  post: Post | Reply;
  superPost?: Post;
  onEdit?: (onEditedEvent: string) => void;
};
export default function ActionsDropdown({
  buttonText,
  dataTestId = null,
  post,
  superPost = null,
  onEdit = () => {}
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const currentUser = useSelector(({
    context
  }) => context.currentUser);
  const community = useSelector(({
    context
  }) => context.currentCommunity);
  if (!post || !currentUser) return null;
  const postType = getPostTypeName(post.postTypeId);
  const sendFootfallEvent = (clickType: string, section?: Section) => {
    const deleteModalWithPostType = (postType + '-more' as Section);
    const metadata = section ? {
      section,
      origin: deleteModalWithPostType
    } : {
      section: deleteModalWithPostType
    };
    trackFootfallEvent(Event.Clicked, {
      clickType: clickType,
      clickMetadata: metadata
    });
  };
  const onSelect = async value => {
    const editPostOrReply = onEdited => {
      if (!userHasPermission(currentUser, Permission.CreatePost)) {
        dispatch(setAlert(i18n.t(`You have been restricted in this community. Please contact this community's administrator to find out why.`)));
        return;
      }
      if (!superPost) {
        onEdited();
        if (post.postTypeId === PostType.Poll) {
          const href = `/community/writePoll?communitySlug=${community.slug}&id=${post.id}`;
          const as = `/${community.slug}/polls/edit/${post.id}`;
          Router.push(href, as);
        } else {
          const href = `/community/write?communitySlug=${community.slug}&id=${post.id}`;
          const as = `/${community.slug}/posts/edit/${post.id}`;
          Router.push(href, as);
        }
      }
      onEdit(onEdited);
    };
    switch (value) {
      case 'edit':
        {
          sendFootfallEvent('edit-post');
          const onEdited = () => sendFootfallEvent('edit-post', Section.Edit);
          editPostOrReply(onEdited);
          break;
        }
      case 'delete':
        {
          sendFootfallEvent('delete');
          if (post.postTypeId === PostType.Poll) {
            dispatch(showModal({
              modalType: ModalType.DeletePoll,
              modalProps: {
                post
              }
            }));
          } else {
            dispatch(showModal({
              modalType: ModalType.DeletePost,
              modalProps: {
                post,
                superPost,
                editPostOrReply
              }
            }));
          }
          break;
        }
      case 'edit-badges':
        dispatch(showModal({
          modalType: ModalType.EditBadges,
          modalProps: {
            member: post.author
          }
        }));
        break;
      case 'restrict':
        {
          const restrictSuccessMessage = i18n.t('User %1$s has been restricted').replace('%1$s', post.author.username);
          dispatch(showModal({
            modalType: ModalType.Default,
            modalProps: {
              acceptButtonText: i18n.t('Restrict'),
              cancelButtonText: i18n.t('Cancel'),
              children: i18n.t('Restricted users cannot create posts or respond to posts in this community, or send private messages to members of this community.'),
              title: i18n.t('Restrict user'),
              onAccept: () => dispatch(restrictUser(post.author, community.slug, restrictSuccessMessage))
            }
          }));
          break;
        }
      case 'pin':
        dispatch(togglePostPin(post.id, community.slug));
        break;
      case 'lock':
        {
          sendFootfallEvent('turn-off-replying');
          const onTurnedOff = () => sendFootfallEvent('turn-off-replying', Section.TurnOffRepliesModal);
          dispatch(showTurnOffRepliesModal(onTurnedOff));
          break;
        }
      case 'unlock':
        {
          const modalProps: ModalProps = {
            acceptButtonText: i18n.t('Yes'),
            cancelButtonText: i18n.t('Cancel'),
            children: i18n.t('This will allow users to create and edit replies.'),
            title: i18n.t('Are you sure you want to do this?'),
            onAccept: async () => {
              await dispatch(post.postTypeId === PostType.Poll ? updatePollLock(post.id, community.slug, UNLOCK) : updatePostLock(post.id, community.slug, UNLOCK));
            }
          };
          dispatch(showModal({
            modalType: ModalType.Default,
            modalProps
          }));
          break;
        }
      case 'sentiment':
        dispatch(showModal({
          modalType: ModalType.SentimentFeedback
        }));
        break;
      case 'copyLink':
        {
          navigator.clipboard.writeText(post.postUrl);
          switch (post.postTypeId) {
            case PostType.Poll:
              dispatch(setAlert(i18n.t('Poll link copied'), 'success'));
              break;
            case PostType.Reply:
              dispatch(setAlert(i18n.t('Reply link copied'), 'success'));
              break;
            default:
              dispatch(setAlert(i18n.t('Post link copied'), 'success'));
              break;
          }
          trackFootfallEvent(Event.Clicked, {
            clickMetadata: {
              section: `${postType}-more`
            },
            clickType: `copy-${postType}-link`
          });
          break;
        }
    }
  };
  const {
    author
  } = post;
  const isModeratorRole = userHasRole(currentUser, Role.Moderator);
  const threadPost = superPost || post;
  const isPoll = threadPost.postTypeId === PostType.Poll;
  const canEdit = (author.id === currentUser.userId || !isPoll && userHasPermission(currentUser, Permission.EditPost) || isPoll && userHasPermission(currentUser, Permission.EditPoll)) && !threadPost.isLocked;
  const canDelete = author.id === currentUser.userId || !isPoll && userHasPermission(currentUser, Permission.DeletePost) || isPoll && userHasPermission(currentUser, Permission.DeletePoll);

  // For locking post and poll ('LockPoll' permission does not exist). Reply cannot be locked
  const canTurnOffReplying = userHasPermission(currentUser, Permission.LockPost) && !superPost;
  const canEditBadges = getCanEditBadges(currentUser.roles, post.author.roles);
  const canRestrict = (!isPoll || isPoll && !isModeratorRole) && userHasPermission(currentUser, Permission.CreateUserRole) && author.id !== currentUser.userId;
  const canPinPost = post.postTypeId === PostType.Post && userHasPermission(currentUser, Permission.PinPost);
  const canGiveSentimentFeedback = !!currentUser && !superPost;
  let label = i18n.t('Copy post link');
  switch (post.postTypeId) {
    case PostType.Poll:
      label = i18n.t('Copy poll link');
      break;
    case PostType.Reply:
      label = i18n.t('Copy reply link');
  }
  const actions = [canEdit && {
    label: i18n.t('Edit'),
    value: 'edit'
  }, canDelete && {
    label: i18n.t('Delete'),
    value: 'delete'
  }, canTurnOffReplying && !post.isLocked && {
    label: i18n.t('Turn off replying'),
    value: 'lock'
  }, canTurnOffReplying && !!post.isLocked && {
    label: i18n.t('Turn on replying'),
    value: 'unlock'
  }, canEditBadges && {
    label: i18n.t('Edit badges'),
    value: 'edit-badges'
  }, canRestrict && {
    label: i18n.t('Restrict user'),
    value: 'restrict'
  }, canPinPost && {
    label: (post as Post)?.isPinned ? i18n.t('Unpin') : i18n.t('Pin'),
    value: 'pin'
  }, canGiveSentimentFeedback && {
    label: i18n.t('Sentiment feedback'),
    value: 'sentiment'
  }, {
    label,
    value: 'copyLink'
  }].filter(x => !!x);
  return <div style={{
    display: 'inline-block'
  }} title={i18n.t('Interact with posts')} data-sentry-component="ActionsDropdown" data-sentry-source-file="actionsDropdown.tsx">
      <ButtonDropdown color="actions" dataTestId={dataTestId} text={buttonText} data-sentry-element="ButtonDropdown" data-sentry-source-file="actionsDropdown.tsx">
        {actions.map(({
        label,
        value
      }) => <DropdownItem data-testid={dataTestId && `${dataTestId}__${value}-button`} key={value} onClick={() => onSelect(value)}>
            {label}
          </DropdownItem>)}
      </ButtonDropdown>
    </div>;
}